<template>
	<div>
		<div class="col-md-12">
			<div class="row">
				<div class="col-md-8">
					<h3 class="page-header">
						<i class="fa fa-plane animated bounceInDown show-info"></i>
						Attendance Leave Types
					</h3>
					<p class="text-muted" style="margin-left:4px">
						Leave types are data, not code. "Leave without pay" is simply a type with pay switched
						off, and comp-off is the type that approved overtime credits.
					</p>
				</div>
				<div class="col-md-4 text-right" style="margin-top:20px">
					<button class="btn btn-primary" @click="create">
						<i class="fa fa-plus"></i> Add leave type
					</button>
				</div>
			</div>
		</div>

		<div class="col-md-12" v-if="error" style="margin-top:10px">
			<div class="alert alert-danger">{{ error }}</div>
		</div>

		<div class="col-md-12 table-responsive" style="padding:0px;margin-top:10px" v-else>
			<div class="panel">
				<div class="panel-body table" style="overflow-x:auto;padding-left:0px">
					<table class="table table-bordered table-hover table-striped display">
						<thead class="table-font">
							<th>Type</th>
							<th>Paid</th>
							<th>Quota / year</th>
							<th>Accrual</th>
							<th>Half day</th>
							<th>Sandwich rule</th>
							<th>Carry forward</th>
							<th></th>
						</thead>
						<tbody>
							<tr v-if="loading">
								<td colspan="8" class="text-center">Loading…</td>
							</tr>
							<tr v-else-if="!rows.length">
								<td colspan="8" class="text-center">No leave types configured yet.</td>
							</tr>
							<tr v-for="row in rows" :key="row.id">
								<td>
									<span class="color-dot" :style="{ background: row.color || '#94a3b8' }"></span>
									<strong>{{ row.name }}</strong>
									<br />
									<small class="text-muted">{{ row.code }}</small>
								</td>
								<td>
									<span class="badge" :class="row.is_paid ? 'bg-success' : 'bg-danger'">
										{{ row.is_paid ? 'Paid' : 'Unpaid' }}
									</span>
								</td>
								<td>{{ Number(row.quota_per_year) || '—' }}</td>
								<td>{{ row.accrual }}</td>
								<td>{{ row.allow_half_day ? 'Yes' : 'No' }}</td>
								<td>
									<span
										class="badge"
										:class="row.sandwich_rule === 'include' ? 'bg-warning text-dark' : 'bg-secondary'"
									>
										{{ row.sandwich_rule }}
									</span>
								</td>
								<td>
									<span v-if="row.carry_forward">Up to {{ Number(row.carry_forward_cap) }}</span>
									<span v-else class="text-muted">No</span>
								</td>
								<td style="white-space:nowrap">
									<button class="btn btn-sm btn-primary" @click="edit(row)" title="Edit">
										<i class="fa fa-pencil"></i>
									</button>
									<button class="btn btn-sm btn-danger" @click="remove(row)" title="Deactivate">
										<i class="fa fa-trash"></i>
									</button>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>

		<modal name="attendance-leavetype" :width="680" :height="'auto'" :adaptive="true" :scrollable="true">
			<div style="padding:18px;max-height:80vh;overflow-y:auto">
				<h4 class="modal-header-title">{{ form.id ? 'Edit leave type' : 'Add leave type' }}</h4>

				<div class="row">
					<div class="col-md-4 form-group">
						<label>Code</label>
						<input type="text" class="form-control form-cascade-control" placeholder="CL" v-model="form.code" />
					</div>
					<div class="col-md-8 form-group">
						<label>Name</label>
						<input type="text" class="form-control form-cascade-control" placeholder="Casual Leave" v-model="form.name" />
					</div>
				</div>
				<div class="row">
					<div class="col-md-4 form-group">
						<label>Colour</label>
						<input type="color" class="form-control form-cascade-control" v-model="form.color" />
					</div>
					<div class="col-md-8">
						<div class="checkbox" style="margin-top:28px">
							<label>
								<input type="checkbox" v-model="form.is_paid" />
								Paid — switch off to make this a leave-without-pay type
							</label>
						</div>
					</div>
				</div>

				<hr />
				<p class="font-weight-bold">Entitlement</p>
				<div class="row">
					<div class="col-md-8 form-group">
						<label>Accrual</label>
						<select class="form-control form-cascade-control" v-model="form.accrual">
							<option value="none">None — balance set manually</option>
							<option value="monthly">Monthly</option>
							<option value="yearly">Yearly (full quota upfront)</option>
							<option value="on_join">On joining</option>
						</select>
					</div>
					<div class="col-md-4 form-group">
						<label>Quota / year</label>
						<input type="number" min="0" max="365" step="0.01" class="form-control form-cascade-control" v-model.number="form.quota_per_year" />
					</div>
				</div>
				<div class="form-group" v-if="form.accrual === 'monthly'">
					<label>Credited each month</label>
					<input type="number" min="0" max="31" step="0.01" class="form-control form-cascade-control" v-model.number="form.accrual_per_period" />
					<small class="text-muted">1.25 per month reaches 15 days a year.</small>
				</div>
				<div class="checkbox">
					<label>
						<input type="checkbox" v-model="form.carry_forward" />
						Carry unused days into next year
					</label>
				</div>
				<div class="form-group" v-if="form.carry_forward">
					<label>Carry-forward cap</label>
					<input type="number" min="0" max="365" class="form-control form-cascade-control" v-model.number="form.carry_forward_cap" />
				</div>

				<hr />
				<p class="font-weight-bold">Rules</p>
				<div class="form-group">
					<label>Weekends and holidays inside a leave span</label>
					<select class="form-control form-cascade-control" v-model="form.sandwich_rule">
						<option value="exclude">Exclude — weekends and holidays are free</option>
						<option value="include">Include — days off in between are consumed too</option>
					</select>
					<small class="text-muted">
						With "include", a Friday plus Monday request costs four days, not two.
					</small>
				</div>
				<div class="row">
					<div class="col-md-4 form-group">
						<label>Min days</label>
						<input type="number" min="0" max="365" step="0.1" class="form-control form-cascade-control" v-model.number="form.min_days" />
					</div>
					<div class="col-md-4 form-group">
						<label>Max per request</label>
						<input type="number" min="0" max="365" class="form-control form-cascade-control" v-model.number="form.max_days_per_request" />
					</div>
					<div class="col-md-4 form-group">
						<label>Max per month</label>
						<input type="number" min="0" max="365" class="form-control form-cascade-control" v-model.number="form.max_per_month" />
					</div>
				</div>
				<div class="row">
					<div class="col-md-4 form-group">
						<label>Notice days</label>
						<input type="number" min="0" max="365" class="form-control form-cascade-control" v-model.number="form.notice_days" />
					</div>
					<div class="col-md-4 form-group">
						<label>Eligible after (days)</label>
						<input type="number" min="0" max="3650" class="form-control form-cascade-control" v-model.number="form.eligible_after_days" />
					</div>
					<div class="col-md-4 form-group">
						<label>Document after (days)</label>
						<input type="number" min="0" max="365" class="form-control form-cascade-control" v-model.number="form.requires_document_after_days" />
					</div>
				</div>
				<div class="checkbox">
					<label>
						<input type="checkbox" v-model="form.allow_half_day" /> Can be taken as a half day
					</label>
				</div>
				<div class="checkbox">
					<label>
						<input type="checkbox" v-model="form.allow_negative_balance" /> Allow going into negative
						balance
					</label>
				</div>
				<div class="form-group" v-if="form.allow_negative_balance">
					<label>Negative cap (days)</label>
					<input type="number" min="0" max="365" class="form-control form-cascade-control" v-model.number="form.negative_cap" />
				</div>

				<div class="text-right" style="margin-top:12px">
					<button class="btn btn-default" @click="$modal.hide('attendance-leavetype')">Cancel</button>
					<button class="btn btn-primary" :disabled="saving" @click="save">Save</button>
				</div>
			</div>
		</modal>
	</div>
</template>

<script>
import { leaveTypeService, errorMessage } from './attendanceService'

export default {
	name: 'AttendanceLeaveTypes',
	data() {
		return {
			rows: [],
			loading: false,
			error: '',
			saving: false,
			form: this.blank(),
		}
	},
	mounted() {
		this.refresh()
	},
	methods: {
		blank() {
			return {
				id: null,
				code: '',
				name: '',
				color: '#3b82f6',
				is_paid: true,
				accrual: 'yearly',
				quota_per_year: 12,
				accrual_per_period: 0,
				carry_forward: false,
				carry_forward_cap: 0,
				encashable: false,
				allow_half_day: true,
				min_days: 0.5,
				max_days_per_request: null,
				max_per_month: null,
				notice_days: 0,
				eligible_after_days: 0,
				requires_document_after_days: null,
				sandwich_rule: 'exclude',
				allow_negative_balance: false,
				negative_cap: 0,
				sort_order: 0,
				is_active: true,
			}
		},
		refresh() {
			this.loading = true
			this.error = ''
			leaveTypeService
				.list()
				.then((data) => {
					this.rows = data || []
				})
				.catch((e) => {
					this.error = errorMessage(e, 'Could not load leave types.')
				})
				.finally(() => {
					this.loading = false
				})
		},
		create() {
			this.form = this.blank()
			this.$modal.show('attendance-leavetype')
		},
		edit(type) {
			this.form = { ...type }
			this.$modal.show('attendance-leavetype')
		},
		save() {
			this.saving = true
			const body = { ...this.form }
			const id = body.id
			delete body.id
			const req = id ? leaveTypeService.update(id, body) : leaveTypeService.create(body)
			req
				.then(() => {
					this.$notify({ text: 'Saved', type: 'success' })
					this.$modal.hide('attendance-leavetype')
					this.refresh()
				})
				.catch((e) => {
					this.$notify({ text: errorMessage(e, 'Could not save'), type: 'error' })
				})
				.finally(() => {
					this.saving = false
				})
		},
		remove(type) {
			if (!window.confirm(`Deactivate "${type.name}"? Existing balances and history are kept.`))
				return
			leaveTypeService
				.remove(type.id)
				.then(() => {
					this.$notify({ text: 'Deactivated', type: 'success' })
					this.refresh()
				})
				.catch((e) => {
					this.$notify({ text: errorMessage(e, 'Failed'), type: 'error' })
				})
		},
	},
}
</script>

<style scoped>
.modal-header-title {
	margin-top: 0;
}
.color-dot {
	display: inline-block;
	width: 12px;
	height: 12px;
	border-radius: 50%;
	margin-right: 6px;
	vertical-align: middle;
}
</style>
